import { Platform } from 'react-native';

import { useSafeViewport } from '../../../utils/hooks/useSafeViewport';
import { useHeaderTopOffset } from '../../Pages/headerOffset';
import { getNavRowHeight } from '../AppFooter/Nav/FooterNavButtonsV2';
import { HEADER_BAR } from '../Top/headerBar';
import { WHEEL_POSE, WheelPose } from './WheelCarousel';

/**
 * The mobile carousel pages - lobby rooms, tournaments and VIP tables - from the Figma section
 * "03_11_Carousel no scroll layout fix" (8526:39564). All three share one stage on the 390x844
 * frame: the top bar ends at 104, a 256x376 card stands 9 under it, the dots start 12 under the
 * card at 501, the page's own blocks end at 744 and the nav starts at 749. Nothing scrolls: the
 * stage is scaled as one piece to the room between the header and the nav, so a short phone gets
 * a smaller stage rather than a page to scroll.
 */
export const STAGE = {
	frameWidth: 390,
	top: 104,
	bottom: 749,
	cardTop: 113,
	cardWidth: 256,
	cardHeight: 376,
	dotsTop: 501,
	contentBottom: 744,
} as const;

export const STAGE_CARD_ASPECT = STAGE.cardHeight / STAGE.cardWidth;

const STAGE_HEIGHT = STAGE.bottom - STAGE.top;
// AppFooter keeps the nav 12 of the 390 frame off the bottom of the screen.
const FOOTER_BOTTOM_GAP = 12;
const NATIVE_TOP_PAD_MAX = 40;

/**
 * The wheel pose for the stage's wider, shorter card. In the design the neighbours stand 58 wide
 * and 277 tall (0.74 of the front card) between the front card and the screen edge, 8 in from it.
 */
export const STAGE_WHEEL_POSE: WheelPose = {
	...WHEEL_POSE,
	rotateDeg: 61.6,
	translateZRatio: -2.68,
	translateXRatio: 0.922,
};

export type CarouselStage = {
	/** Design units to points. */
	scale: number;
	s: (value: number) => number;
	/** Where design y `STAGE.top` (the header's bottom edge) lands in the page. */
	top: number;
	/** A design y on the 844 frame as a top offset in the page. */
	y: (designY: number) => number;
	width: number;
	cardWidth: number;
	cardHeight: number;
};

/**
 * Lays out a `fullBleed` page's carousel stage: the page spans the screen and the header and the
 * nav are drawn over it, so the stage sits in the band between them.
 */
export const useCarouselStage = (): CarouselStage => {
	const { width, height, insets } = useSafeViewport();
	const headerTopOffset = useHeaderTopOffset();

	const widthScale = width / STAGE.frameWidth;
	// MainLayout.native pads the screen (status bar hidden) by up to 40 of the top inset before the
	// header; on web the header is lowered by the header offset instead.
	const screenTop = Platform.OS === 'web' ? 0 : Math.min(insets.top, NATIVE_TOP_PAD_MAX);
	const bandTop = screenTop + headerTopOffset + HEADER_BAR.top + HEADER_BAR.height;
	// The footer stands on the bottom of the screen itself, not above the bottom inset.
	const bandBottom = height - FOOTER_BOTTOM_GAP * widthScale - getNavRowHeight(width, height, true);
	const band = Math.max(bandBottom - bandTop, 0);
	const scale = Math.min(widthScale, band / STAGE_HEIGHT);
	// A screen taller than the design keeps the stage in the middle of the band.
	const top = bandTop + (band - STAGE_HEIGHT * scale) / 2;
	const s = (value: number) => value * scale;

	return {
		scale,
		s,
		top,
		y: (designY: number) => top + (designY - STAGE.top) * scale,
		width,
		cardWidth: s(STAGE.cardWidth),
		cardHeight: s(STAGE.cardHeight),
	};
};
